import type { SvgIcon } from '@mui/material';
import { t } from 'i18next';
import { APPLICATION_TRACE_TYPES, TRACE_TYPES_WITH_OPERATIONS, TraceType } from '../common/enums/TraceType';

export interface Route {
  name: string;
  path: string;
  nestedPaths?: string[];
  children?: Route[];
  Icon?: typeof SvgIcon;
  openInNewTab?: boolean;
  divider?: boolean;
  onClick?: () => void;
  'data-role'?: string;
}

/**
 * Routes used for router.
 * These are active routes currently used in the app.
 * Any additional, removal or change should be handled in `Router.tsx` with
 * associated controllers or redirections.
 */
export enum PATHS {
  ROOT = '/',
  ORGANIZATION_DASHBOARD = '/organization/dashboard',
  ORGANIZATION_PROJECTS = '/organization/projects',
  USERS = '/organization/users',
  INTEGRATIONS = '/organization/integrations',
  INTEGRATION_JIRA = `${PATHS.INTEGRATIONS}/jira`,
  INTEGRATION_JIRA_FORM = `${PATHS.INTEGRATIONS}/jira_form`,
  INTEGRATION_TANIUM = `${PATHS.INTEGRATIONS}/tanium`,
  INTEGRATION_VENAFI = `${PATHS.INTEGRATIONS}/venafi`,
  INTEGRATION_CROWDSTRIKE = `${PATHS.INTEGRATIONS}/crowdstrike`,
  INTEGRATION_SERVICE_NOW = `${PATHS.INTEGRATIONS}/service-now`,
  PROFILES = '/organization/profiles',
  PROFILES_CREATE = '/organization/profiles/create',
  PROFILES_CREATE_TEMPLATE = '/organization/profiles/create/:profileId',
  PROFILE_DETAIL = '/organization/profiles/:profileId/detail',
  KEYS = '/organization/keys',
  KEY = '/organization/key/:keyId',
  CERTIFICATES = '/organization/certificates',
  CERTIFICATE = '/organization/certificate/:certificateId',
  SENSORS = '/organization/sensors',
  SCANS = '/organization/scans',
  SCANS_CROWDSTRIKE = `${PATHS.SCANS}/crowdstrike`,
  PROJECT = '/project/:projectId', // Does not map a CS route, used for React Routing
  PROJECT_DASHBOARD = '/project/:projectId/dashboard',
  PROJECT_SLOTS = '/project/:projectId/slots',
  PROJECT_FILTERS = '/project/:projectId/filters',
  PROJECT_INTEGRATIONS = '/project/:projectId/integrations',
  REPORT = '/report/:reportId',
  REPORT_INVENTORY = '/report/:reportId/inventory',
  REPORT_ISSUE = '/report/:reportId/:issueId',
  REPORT_ISSUE_INSTANCE = '/report/:reportId/:issueId/:instanceId',
  REPORT_KEY_LIFE_CYCLES = '/report/:reportId/keys',
  REPORT_KEY_LIFE_CYCLE = '/report/:reportId/keys/:keyId',
  REPORT_OPERATIONS = '/report/:reportId/operations',
  REPORT_OPERATION = '/report/:reportId/operations/:operationId',
  REPORT_CERTIFICATES = '/report/:reportId/certificates',
  REPORT_CERTIFICATE = '/report/:reportId/certificates/:certificateId',
  REPORT_CALL_SITES = '/report/:reportId/call-sites',
  REPORT_DIFF = '/report/:reportId/compare',
  REPORT_HANDSHAKES = '/report/:reportId/handshakes',
  REPORT_TLS_HANDSHAKES = `${PATHS.REPORT_HANDSHAKES}/tls`,
  REPORT_SSH_HANDSHAKES = `${PATHS.REPORT_HANDSHAKES}/ssh`,
  REPORT_CIPHERSUITES = '/report/:reportId/ciphersuites',
  API_EXPLORER = '/api/explorer',
  ACCESSIBILITY_STATEMENT = '/accessibility-statement',
  USE_CONDITIONS_STATEMENT = '/use-conditions-statement',

  LOGIN_PAGE = '/login',
  LOGOUT_PAGE = '/logout',
  SESSION_PICKUP_PAGE = '/session-pickup',
}

export enum EXTERNAL_URLS {
  DOCS_BASE = '/docs',
  DOCS_API = '/api/',
  DOCS_CALL_SITE_STATUS = '/fundamentals/callsite-fundamentals/#call-site-status',
  DOCS_CHANGELOG = '/changelog/',
  DOCS_CREATE_EXCEPTIONS = '/projects/add-or-remove-an-exception/#exceptions-considerations',
  DOCS_EXCEPTIONS = '/fundamentals/exception-fundamentals/',
  DOCS_HOME = '/',
  DOCS_HOST_SCANNER_LINUX = '/sensors/filesystem-scanner/linux-getting-started/',
  DOCS_HOST_SCANNER_WINDOWS = '/sensors/filesystem-scanner/windows-getting-started/',
  DOCS_JAVA_TRACER = '/sensors/java-tracer/',
  DOCS_JAVA_SCANNER = '/sensors/java-bytecode-scanner/',
  DOCS_JIRA_FORM_INTEGRATION = '/integrations/jira/form-integration/',
  DOCS_JIRA_ISSUE_EXPORT = '/integrations/jira/issue-export/',
  DOCS_JIRA_ISSUE_COLLECTOR = '/integrations/jira/form-integration/#configure-the-issue-collector-in-jira',
  DOCS_NETWORK_ANALYZER = '/sensors/network-analyzer/',
  DOCS_SENSORS = '/sensors/',
  DOCS_KB_JAVA_KEYSTORES = '/knowledge-base/java-keystores/',
  DOCS_KB_JAVA_HASH_FUNCTIONS = '/knowledge-base/hash-functions/',
  DOCS_SCANS_CROWDSTRIKE_PARAMS = '/integrations/crowdstrike/getting-started/#ingest-crowdstrike-data',
  DOCS_SCANS_PAGE_TOUR = '/scans/#scans-page-tour',
  VENAFI_VIEW_CERTIFICATE = '/aperture/certificate/',
}

export enum TabNameLocalizationKeys {
  API = 'api',
  HOME = 'home',
  OVERVIEW = 'overview',
  ISSUES = 'issues',
  CALL_SITES = 'callSites',
  COMPARE_REPORT = 'compare',
  REPORTS = 'reports',
  TRACES = 'traces',
  DASHBOARD = 'dashboard',
  SLOTS = 'slots',
  EXCEPTIONS = 'exceptions',
  PROJECTS = 'projects',
  USERS = 'users',
  INTEGRATIONS = 'integrations',
  PROFILES = 'profiles',
  KEYS = 'keys',
  CERTIFICATES = 'certificates',
  REPORT_CERTIFICATES = 'reportCertificates',
  SETTINGS = 'settings',
  SCANS = 'scans',
  SUMMARY = 'summary',
  TANIUM = 'tanium',
  SENSORS = 'sensors',
  HANDSHAKES = 'handshakes.tabName',
  CIPHERSUITES = 'ciphersuites.tabName',
  JIRA = 'jira',
  VENAFI = 'venafi',
  CROWDSTRIKE = 'crowdstrike',
  OPERATIONS = 'operations',
  SERVICE_NOW = 'serviceNow',
}

/**
 * Used to validate that a report tab is compatible with its `traceType`.
 * When `REPORT_TABS` is called to define the routes available in the application
 * `traceType === undefined`. In this case all paths need to be considered compatible
 * to mount the routing tree.
 *
 * When `REPORT_TABS` is called from `ReportController` `traceType` is `TraceType | null`
 * and it represents the actual `traceType` of a single report. In this case, only
 * compatible tabs need to be evaluated.
 */
const compatibleTraceType = (traceType: TraceType | null | undefined, traceTypes: TraceType[]) => {
  return traceType === undefined || (traceType && traceTypes.includes(traceType));
};

export const REPORT_TABS = (traceType?: TraceType | null): Route[] => {
  const tabs: Route[] = [
    {
      name: t(TabNameLocalizationKeys.SUMMARY),
      path: PATHS.REPORT_INVENTORY,
    },
    {
      name: t(TabNameLocalizationKeys.ISSUES),
      path: PATHS.REPORT,
      nestedPaths: [PATHS.REPORT_ISSUE, PATHS.REPORT_ISSUE_INSTANCE],
    },
  ];
  if (compatibleTraceType(traceType, [TraceType.HOST_SCANNER, ...TRACE_TYPES_WITH_OPERATIONS])) {
    tabs.push({
      name: t(TabNameLocalizationKeys.KEYS),
      path: PATHS.REPORT_KEY_LIFE_CYCLES,
      nestedPaths: [PATHS.REPORT_KEY_LIFE_CYCLE],
    });
  }
  if (compatibleTraceType(traceType, [TraceType.JAVA, TraceType.HOST_SCANNER, TraceType.NETWORK])) {
    tabs.push({
      name: t(TabNameLocalizationKeys.REPORT_CERTIFICATES),
      path: PATHS.REPORT_CERTIFICATES,
      nestedPaths: [PATHS.REPORT_CERTIFICATE],
    });
  }
  if (compatibleTraceType(traceType, TRACE_TYPES_WITH_OPERATIONS)) {
    tabs.push({
      name: t(TabNameLocalizationKeys.OPERATIONS),
      path: PATHS.REPORT_OPERATIONS,
      nestedPaths: [PATHS.REPORT_OPERATION],
    });
  }
  if (compatibleTraceType(traceType, [TraceType.JAVA])) {
    tabs.push({
      name: t(TabNameLocalizationKeys.CALL_SITES),
      path: PATHS.REPORT_CALL_SITES,
    });
  }
  if (compatibleTraceType(traceType, [TraceType.NETWORK])) {
    tabs.push({
      name: t(TabNameLocalizationKeys.CIPHERSUITES),
      path: PATHS.REPORT_CIPHERSUITES,
    });
    tabs.push({
      name: t(TabNameLocalizationKeys.HANDSHAKES),
      path: PATHS.REPORT_HANDSHAKES,
      nestedPaths: [PATHS.REPORT_TLS_HANDSHAKES, PATHS.REPORT_SSH_HANDSHAKES],
    });
  }
  if (compatibleTraceType(traceType, APPLICATION_TRACE_TYPES)) {
    tabs.push({
      name: t(TabNameLocalizationKeys.COMPARE_REPORT),
      path: PATHS.REPORT_DIFF,
    });
  }

  return tabs;
};

export const PROJECT_TABS = (): Route[] => {
  return [
    {
      name: t(TabNameLocalizationKeys.OVERVIEW),
      path: PATHS.PROJECT_DASHBOARD,
    },
    {
      name: t(TabNameLocalizationKeys.REPORTS),
      path: PATHS.PROJECT_SLOTS,
    },
    {
      name: t(TabNameLocalizationKeys.EXCEPTIONS),
      path: PATHS.PROJECT_FILTERS,
    },
    {
      name: t(TabNameLocalizationKeys.INTEGRATIONS),
      path: PATHS.PROJECT_INTEGRATIONS,
    },
  ];
};

export const SCANS_TABS = (): Route[] => {
  const tabs: Route[] = [{ name: t(TabNameLocalizationKeys.CROWDSTRIKE), path: PATHS.SCANS_CROWDSTRIKE }];
  return tabs;
};

export const getFlatPaths = (routes: Route[]) => routes.reduce((paths, tab) => [...paths, tab.path, ...(tab.nestedPaths || [])], [] as string[]);

type MainNavigationProps = {
  canReadOrganizationKeys?: boolean;
  canReadOrganizationCertificates?: boolean;
  canReadOrganizationUsers?: boolean;
  canReadSensors?: boolean;
  canReadScans?: boolean;
};

export const MAIN_NAV_TABS = ({
  canReadOrganizationKeys = true,
  canReadOrganizationCertificates = true,
  canReadOrganizationUsers = true,
  canReadSensors = true,
  canReadScans = true,
}: MainNavigationProps = {}): Route[] => {
  const routes: Route[] = [
    {
      name: t(TabNameLocalizationKeys.DASHBOARD),
      path: PATHS.ORGANIZATION_DASHBOARD,
    },
    {
      name: t(TabNameLocalizationKeys.PROJECTS),
      path: PATHS.ORGANIZATION_PROJECTS,
      nestedPaths: [...getFlatPaths(PROJECT_TABS()), ...getFlatPaths(REPORT_TABS())],
    },
  ];

  if (canReadOrganizationKeys) {
    routes.push({
      name: t(TabNameLocalizationKeys.KEYS),
      path: PATHS.KEYS,
      nestedPaths: [PATHS.KEY],
    });
  }
  if (canReadOrganizationCertificates) {
    routes.push({
      name: t(TabNameLocalizationKeys.CERTIFICATES),
      path: PATHS.CERTIFICATES,
      nestedPaths: [PATHS.CERTIFICATE],
    });
  }

  const settingsChildren: Route[] = [];
  if (canReadOrganizationUsers) {
    settingsChildren.push({
      name: t(TabNameLocalizationKeys.USERS),
      path: PATHS.USERS,
    });
  }
  settingsChildren.push({
    name: t(TabNameLocalizationKeys.INTEGRATIONS),
    path: PATHS.INTEGRATIONS,
    nestedPaths: [
      PATHS.INTEGRATION_JIRA_FORM,
      PATHS.INTEGRATION_TANIUM,
      PATHS.INTEGRATION_VENAFI,
      PATHS.INTEGRATION_JIRA,
      PATHS.INTEGRATION_SERVICE_NOW,
      PATHS.INTEGRATION_CROWDSTRIKE,
    ],
  });
  settingsChildren.push({
    name: t(TabNameLocalizationKeys.PROFILES),
    path: PATHS.PROFILES,
    nestedPaths: [PATHS.PROFILE_DETAIL, PATHS.PROFILES_CREATE, PATHS.PROFILES_CREATE_TEMPLATE],
  });
  if (canReadSensors) {
    settingsChildren.push({
      name: t(TabNameLocalizationKeys.SENSORS),
      path: PATHS.SENSORS,
    });
  }
  // Scans tab only contains CrowdStrike, do not show if either flag is disabled
  if (canReadScans) {
    settingsChildren.push({
      name: t(TabNameLocalizationKeys.SCANS),
      path: PATHS.SCANS,
      nestedPaths: [PATHS.SCANS_CROWDSTRIKE],
    });
  }
  settingsChildren.push({
    name: t(TabNameLocalizationKeys.API),
    path: PATHS.API_EXPLORER,
  });
  routes.push({
    name: t(TabNameLocalizationKeys.SETTINGS),
    path: '',
    children: settingsChildren,
    nestedPaths: settingsChildren.flatMap(({ path, nestedPaths = [] }) => [path, ...nestedPaths]),
  });

  return routes;
};

/**
 * Paths that are not currently used in the app, but were used during the SPA migration.
 * Should only be used for redirections in case users try to access them directly.
 */
export enum LEGACY_PATHS {
  ORGANIZATION_PROJECT = '/organization/projects/:projectId',
  USERS_CREATE = '/organization/users/create',
  USER = '/organization/users/:userId',
  USER_RESET_PASSWORD = '/organization/users/:userId/reset_password',
  USER_REMOVE = '/organization/users/:userId/remove',
  CHANGE_PASSWORD = '/change_password',
  SETTINGS_JIRA = '/settings/jira',
  SETTINGS_JIRA_UNLINK = '/settings/jira/unlink',
  SETTINGS_JIRA_TOKEN = '/settings/jira/request_token',
  SETTINGS_JIRA_CALLBACK = '/settings/jira/callback/',
  PROFILE = '/organization/profiles/:profileId',
  PROFILE_EDIT = '/organization/profiles/:profileId/edit',
  PROFILE_REMOVE = '/organization/profiles/:profileId/remove',
  PROJECT_NEW = '/project/new',
  PROJECT_DELETE = '/project/:projectId/delete',
  PROJECT_SLOT = '/project/:projectId/slots/:slotId',
  PROJECT_SLOTS_NEW = '/project/:projectId/slots/new',
  PROJECT_SLOT_DELETE = '/project/:projectId/slots/:slotId/delete',
  PROJECT_FILTERS_CREATE = '/project/:projectId/filters/create',
  PROJECT_FILTER_EDIT = '/project/:projectId/filters/:filterId/edit',
  PROJECT_FILTER_REMOVE = '/project/:projectId/filters/:filterId/remove',
  PROJECT_FILTER_TOGGLE = '/project/:projectId/filters/:filterId/toggle',
  PROJECT_TANIUM = '/project/:projectId/tanium',
  PROJECT_TANIUM_COLLECTOR = '/project/:projectId/tanium/traces_collector',
  PROJECT_TANIUM_STOP_COLLECTOR = '/project/:projectId/tanium/stop_traces_collector',
  PROJECT_TRACES = '/project/:projectId/traces',
  PROJECT_TRACE = '/project/:projectId/traces/:traceId',
  PROJECT_TRACE_EDIT = '/project/:projectId/traces/:traceId/edit',
  PROJECT_TRACE_REMOVE = '/project/:projectId/traces/:traceId/remove',
  PROJECT_REPORTS = '/project/:projectId/reports',
  PROJECT_REPORT = '/project/:projectId/reports/:reportId',
  PROJECT_REPORT_EDIT = '/project/:projectId/reports/:reportId/edit/:fieldName',
  PROJECT_REPORT_DIFFS = '/project/:projectId/reports/:reportId/diffs',
  SLOT_REPORT = '/slot/:slotId/report/:reportId',
  SLOT_REPORT_EDIT = '/slot/:slotId/report/:reportId/edit/:fieldName',
  SLOT_REPORT_DIFFS = '/slot/:slotId/report/:reportId/diffs',
  REPORT_REMOVE = '/report/:reportId/remove',
  REPORT_ISSUE_INSTANCE_JIRA = '/report/:reportId/:issueId/:instanceId/jira',
  REPORT_ISSUE_INSTANCE_EDIT = '/report/:reportId/:issueId/:instanceId/edit',
  LEGACY_REPORT_DIFF = '/report_diff/:reportId/:comparedReportId',
  REPORT_CALL_SITES_LEGACY = '/report/:reportId/call_sites',
  REPORT_EXPORT = '/report/:reportId/print',
  REPORT_EXPORT_PRINT = '/report/:reportId/print?limit=:limit',
  REPORT_EXPORT_JIRA = '/report/:reportId/jira',
  REPORT_EXPORT_JIRA_LIMIT = '/report/:reportId/jira?limit=:limit',
  REPORT_EXPORT_JSON = '/report/:reportId/json',
  REPORT_EXPORT_CSV_ALL = '/report/:reportId/csv',
  REPORT_EXPORT_CSV_SUMMARY = '/report/:reportId/operation_stats_csv',
  REPORT_EXPORT_CSV_OPERATIONS = '/report/:reportId/statistics_csv',
  REPORT_KEY_LIFE_CYCLE_OPERATION = '/report/:reportId/keys/:keyId/operation/:operationId',
  REPORT_ISSUE_INSTANCE_OPERATION = '/report/:reportId/:issueId/:instanceId/:operationId',
  KNOWLEDGE_BASE = '/knowledge-base',
  API = '/api/',
  API_RESET_KEY = '/api/reset_key',
}
